"use client";
import React, { useState, useEffect } from "react";
import type { TestimonialProps } from "@/types/sections/testimonial";
import { getBackgroundStyle } from "@/lib/background";

interface Props {
  data: TestimonialProps;
}

export default function TestimonialCarousel({ data }: Props) {
  const {
    title,
    subtitle,
    quotes = [],
    align = "center",
    background = {},
    extra = {},
  } = data || {};

  const [index, setIndex] = useState(0);
  const autoplay = extra?.carousel?.autoplay ?? true;
  const interval = extra?.carousel?.interval ?? 5000;

  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % quotes.length);
    }, interval);
    return () => clearInterval(timer);
  }, [quotes.length, autoplay, interval]);

  const quote = quotes[index] || {};

  const bgStyle = getBackgroundStyle(background);

  return (
    <section className="py-20 px-4" style={bgStyle}>
      <div className="max-w-2xl mx-auto text-center">
        {title && <h2 className="text-3xl font-bold mb-2">{title}</h2>}
        {subtitle && <p className="text-muted-foreground mb-6">{subtitle}</p>}

        <div className="bg-white p-8 rounded-xl shadow-lg">
          <p className="text-lg italic text-foreground">“{quote.quote}”</p>
          <div className="mt-4 flex justify-center items-center gap-4">
            {quote.avatar && (
              <img
                src={quote.avatar}
                alt={quote.author}
                className="w-12 h-12 rounded-full object-cover"
              />
            )}
            <div>
              <p className="font-semibold">{quote.author}</p>
              <p className="text-sm text-muted-foreground">{quote.role}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-4 gap-2">
          {quotes.map((_, i) => (
            <button
              key={i}
              className={`w-3 h-3 rounded-full ${
                i === index ? "bg-primary" : "bg-secondary"
              }`}
              onClick={() => setIndex(i)}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}
